// Terms & Conditions — content data + LegalLayout renderer.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "eligibility", title: "Eligibility",
    body: (<>
      <p>You must be at least 16 years old (or the minimum legal age in your country) to use CareerOS-AI.</p>
      <p>If you are under the age of majority in your jurisdiction, you must have permission from a parent or legal guardian.</p>
    </>) },
  { n: 2, id: "description-of-service", title: "Description of the Service",
    body: (<>
      <p>CareerOS-AI provides AI-powered career development tools, including but not limited to:</p>
      {bullets(["Career planning","Resume analysis","Resume building","Cover letter generation","Interview preparation","Salary insights","Career roadmaps","Skills assessments","Financial planning tools","Job search assistance","AI chat and coaching","Educational recommendations","Professional development tracking","Goal planning","Career analytics","Premium subscription features"])}
      <p>CareerOS-AI is designed to assist users in making informed career decisions but does not guarantee employment, salary increases, interviews, promotions, or financial outcomes.</p>
    </>) },
  { n: 3, id: "ai-disclaimer", title: "AI Disclaimer",
    body: (<>
      <p>CareerOS-AI uses artificial intelligence to generate recommendations.</p>
      <p>While we strive for accuracy:</p>
      {bullets(["AI can make mistakes.","Recommendations may be incomplete or outdated.","Users should independently verify important information.","CareerOS-AI is not a substitute for professional legal, financial, tax, immigration, psychological, or career advice."])}
      <p>You acknowledge that you use AI-generated content at your own discretion and risk.</p>
    </>) },
  { n: 4, id: "no-employment-guarantee", title: "No Employment Guarantee",
    body: (<>
      <p>CareerOS-AI does not:</p>
      {bullets(["guarantee employment","guarantee job interviews","guarantee promotions","guarantee salary increases","guarantee career success","guarantee acceptance into educational institutions"])}
      <p>Career outcomes depend on numerous factors beyond our control.</p>
    </>) },
  { n: 5, id: "user-accounts", title: "User Accounts",
    body: (<>
      <p>You agree to:</p>
      {bullets(["provide accurate information","keep your password secure","maintain current profile information","notify us of unauthorized access","accept responsibility for all activity on your account"])}
      <p>You are responsible for protecting your login credentials.</p>
    </>) },
  { n: 6, id: "acceptable-use", title: "Acceptable Use",
    body: (<>
      <p>You agree not to:</p>
      {bullets(["violate any law","impersonate another person","upload malicious software","interfere with the Service","scrape or copy our database","reverse engineer our software","attempt unauthorized access","distribute spam","upload unlawful or offensive content","exploit bugs or vulnerabilities","use CareerOS-AI to train competing AI systems without permission"])}
      <p>See our full <Link to="/acceptable-use">Acceptable Use Policy</Link> for details.</p>
    </>) },
  { n: 7, id: "user-content", title: "User Content",
    body: (<>
      <p>You retain ownership of content you upload, including:</p>
      {bullets(["resumes","cover letters","career history","profile information","certifications","portfolios","documents"])}
      <p>You grant CareerOS-AI a limited license to process this information solely for providing the Service.</p>
    </>) },
  { n: 8, id: "intellectual-property", title: "Intellectual Property",
    body: (<>
      <p>All software, branding, algorithms, graphics, AI prompts, workflows, databases, text, designs, trademarks, and content are owned by CareerOS-AI or its licensors.</p>
      <p>You may not:</p>
      {bullets(["copy","reproduce","modify","redistribute","sell","license","reverse engineer"])}
      <p>any part of the Service without written permission.</p>
    </>) },
  { n: 9, id: "subscription-plans", title: "Subscription Plans",
    body: (<>
      <p>CareerOS-AI may offer:</p>
      {bullets(["Free","Pro","Premium"])}
      <p>Subscription benefits, pricing, and feature availability may change over time.</p>
      <p>Subscriptions automatically renew unless cancelled before the renewal date. See our <Link to="/subscription-policy">Subscription &amp; Billing Policy</Link>.</p>
    </>) },
  { n: 10, id: "payments", title: "Payments",
    body: (<>
      <p>Payments are processed securely by third-party payment providers.</p>
      <p>You authorize recurring billing where applicable.</p>
      <p>Failure to pay may result in suspension or cancellation of premium features.</p>
    </>) },
  { n: 11, id: "refund-policy", title: "Refund Policy",
    body: (<>
      <p>Unless required by applicable law:</p>
      {bullets(["subscription fees are non-refundable","partial months are not refunded","cancelling prevents future renewals but does not automatically refund past payments"])}
      <p>If a billing error occurs, please contact support. Full details in our <Link to="/refund-policy">Refund Policy</Link>.</p>
    </>) },
  { n: 12, id: "financial-information", title: "Career and Financial Information",
    body: (<>
      <p>CareerOS-AI may provide:</p>
      {bullets(["salary estimates","retirement projections","investment simulations","tax estimates","career income forecasts"])}
      <p>These are estimates only.</p>
      <p>They should never be relied upon as financial, tax, accounting, investment, or legal advice.</p>
    </>) },
  { n: 13, id: "third-party-services", title: "Third-Party Services",
    body: (<>
      <p>CareerOS-AI may integrate with third-party services such as:</p>
      {bullets(["LinkedIn","Google","Microsoft","Stripe","OpenAI","job boards","educational platforms"])}
      <p>We are not responsible for third-party services or their availability.</p>
      <p>Your use of third-party services is governed by their own terms.</p>
    </>) },
  { n: 14, id: "availability", title: "Availability",
    body: (<>
      <p>We strive for reliable service but do not guarantee uninterrupted availability.</p>
      <p>Maintenance, updates, outages, and technical issues may temporarily affect access.</p>
    </>) },
  { n: 15, id: "beta-features", title: "Beta Features",
    body: (<>
      <p>Some features may be marked as:</p>
      {bullets(["Beta","Experimental","Early Access"])}
      <p>These may contain bugs and may change or be removed without notice.</p>
    </>) },
  { n: 16, id: "limitation-of-liability", title: "Limitation of Liability",
    body: (<>
      <p>To the maximum extent permitted by law, CareerOS-AI shall not be liable for:</p>
      {bullets(["lost income","lost employment opportunities","business interruption","lost profits","indirect damages","incidental damages","consequential damages","reliance on AI-generated information"])}
      <p>Your sole remedy for dissatisfaction is to discontinue use of the Service.</p>
    </>) },
  { n: 17, id: "indemnification", title: "Indemnification",
    body: (<>
      <p>You agree to indemnify and hold harmless CareerOS-AI, its owners, employees, contractors, affiliates, and partners from claims arising out of:</p>
      {bullets(["your misuse of the Service","your violation of these Terms","your violation of applicable laws","your infringement of another person's rights"])}
    </>) },
  { n: 18, id: "privacy", title: "Privacy",
    body: (<>
      <p>Your use of CareerOS-AI is also governed by our <Link to="/privacy">Privacy Policy</Link>.</p>
      <p>By using the Service, you consent to the collection and processing of information described in that policy.</p>
    </>) },
  { n: 19, id: "account-suspension", title: "Account Suspension",
    body: (<>
      <p>We reserve the right to suspend or terminate accounts that:</p>
      {bullets(["violate these Terms","engage in fraud","abuse the platform","threaten security","violate applicable laws"])}
    </>) },
  { n: 20, id: "changes-to-service", title: "Changes to the Service",
    body: (<>
      <p>We may:</p>
      {bullets(["add features","remove features","modify pricing","improve AI models","discontinue parts of the Service"])}
      <p>We are not obligated to maintain every feature indefinitely.</p>
    </>) },
  { n: 21, id: "governing-law", title: "Governing Law",
    body: (<>
      <p>These Terms shall be governed by the laws of the Province of Ontario, Canada, and the applicable federal laws of Canada, without regard to conflict of law principles.</p>
      <p>Any disputes shall be resolved exclusively in the courts located in Ontario, unless otherwise required by law.</p>
    </>) },
  { n: 22, id: "force-majeure", title: "Force Majeure",
    body: <p>CareerOS-AI shall not be liable for delays or failures resulting from causes beyond our reasonable control, including natural disasters, internet outages, cyberattacks, governmental actions, labour disputes, or failures of third-party providers.</p> },
  { n: 23, id: "severability", title: "Severability",
    body: <p>If any provision of these Terms is found unenforceable, the remaining provisions shall continue in full force and effect.</p> },
  { n: 24, id: "entire-agreement", title: "Entire Agreement",
    body: <p>These Terms constitute the entire agreement between you and CareerOS-AI regarding use of the Service and supersede any prior agreements.</p> },
  { n: 25, id: "changes-to-terms", title: "Changes to These Terms",
    body: (<>
      <p>We may update these Terms from time to time.</p>
      <p>Material changes will be communicated through the website, app, or email where required by law.</p>
      <p>Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.</p>
    </>) },
  { n: 26, id: "contact-us", title: "Contact Us",
    body: (<>
      <p>If you have any questions regarding these Terms, please contact us.</p>
      <p className="not-prose mt-4 rounded-lg border border-border p-4 bg-card/60 font-mono text-sm leading-relaxed">
        CareerOS-AI<br />
        Email: <a className="underline" href="mailto:support@careeros-ai.com">support@careeros-ai.com</a><br />
        Website: <a className="underline" href="https://www.careeros-ai.com">https://www.careeros-ai.com</a>
      </p>
    </>) },
];

const INTRO = (
  <>
    <p>
      Welcome to CareerOS-AI (&quot;CareerOS-AI&quot;, &quot;CareerOS&quot;, &quot;we&quot;,
      &quot;our&quot;, or &quot;us&quot;). These Terms and Conditions (&quot;Terms&quot;) govern
      your access to and use of our website, mobile applications, software, AI tools, and related
      services (collectively, the &quot;Service&quot;).
    </p>
    <p>
      By creating an account or using CareerOS-AI, you agree to be bound by these Terms.
      If you do not agree, you may not use the Service.
    </p>
  </>
);

export default function Terms() {
  return (
    <LegalLayout
      eyebrow="Legal · Terms"
      title="Terms and Conditions"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/terms"
    />
  );
}
